import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  AccountTree as DependencyIcon,
  Sync as SyncIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import * as api from '../../api/api';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import UnsavedChangesModal from '../UnsavedChangesModal';


const allowedStatuses = [
  "To Do", "Backlog", "In Progress", "Ready for Review", "Ready for ITGC",
  "Funded", "Not Funded", "Delayed", "Completed"
];

const CAPEX_THRESHOLD = 500000; // $500k threshold

function OutcomeInfoStep({
  data,
  onUpdate,
  onSave,
  loading,
  costEstimationData = {},
  financialModelData = {},
  staffingData = {},
  setSavingOutcome,
  readOnly = false,
  hideButtons = false
}) {
  const theme = useTheme();

  const getDefaultDates = () => {
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    return {
      start_date: `${nextYear}-01-01`,
      end_date: `${nextYear}-12-31`
    };
  };

  const generateMilestoneId = () => {
    return `milestone-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const getDefaultMilestone = useCallback((outcomeYear) => {
    return {
      id: generateMilestoneId(),
      name: 'Milestone #1',
      start_date: `${outcomeYear}-01-01`,
      end_date: `${outcomeYear}-12-31`,
      cost: 0
    };
  }, []);

            const [formData, setFormData] = useState({
    name: '',
    description: '',
    risks: '',
    dependencies: '',
    status: 'To Do',
    start_date: '',
    end_date: '',
    approved_budget: {
      capex: 0,
      opex: 0
    },
    department_budgets: [],
            applications: [],
        tags: [],
        milestones: [],
    functional_owner: '',
    it_owner: '',
    project_code: '',
    project_name: '',
    department: ''
  });

  const [localLoading, setLocalLoading] = useState(false);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
    const [availableApplications, setAvailableApplications] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [functionalOwners, setFunctionalOwners] = useState([]);
  const [itOwners, setItOwners] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [newApplicationDialogOpen, setNewApplicationDialogOpen] = useState(false);
  const [newApplicationForm, setNewApplicationForm] = useState({ name: '', status: 'Active' });
  const [newTagDialogOpen, setNewTagDialogOpen] = useState(false);
  const [newTagForm, setNewTagForm] = useState({ name: '' });
  const [milestoneForm, setMilestoneForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    cost: 0,
    capex_classification: 'OPEX'
  });

  // Debounce ref for onUpdate calls
  const updateTimeoutRef = useRef(null);
  
  // Local editing state to prevent parent re-renders from interfering
  const [localEditingValues, setLocalEditingValues] = useState({});

    // Track if we've initialized to prevent infinite loops
  const initializedRef = useRef(false);

    // Function to fetch milestone costs from staffing data (called only when needed)
  const fetchMilestoneCosts = useCallback(async () => {
    if (!data?.id || !formData.milestones?.length) {
      console.log('OutcomeInfoStep: Skipping milestone cost fetch - no outcome ID or milestones');
      return;
    }
    
    try {
      console.log('OutcomeInfoStep: Fetching milestone costs for outcome:', data.id);
      
      // Try to get staffing data to calculate milestone costs
      const staffingResponse = await api.getStaffingData(data.id);
      
      if (staffingResponse?.milestones) {
        console.log('OutcomeInfoStep: Found staffing data with milestones:', 
          staffingResponse.milestones.map(m => ({ id: m.id, name: m.name, rolesCount: m.roles?.length || 0 })));
        
        // Calculate costs for each milestone
        const milestonesWithCosts = staffingResponse.milestones.map(staffingMilestone => {
          let totalCost = 0;
          
          // Calculate cost from roles
          (staffingMilestone.roles || []).forEach(role => {
            const monthlyRate = role.rate || 0;
            const allocation = Math.max(0, Math.min(100, role.allocation || 0)) / 100;
            const months = Math.max(1, role.overlap_months || 1);
            const roleCost = monthlyRate * allocation * months;
            
            if (!isNaN(roleCost) && roleCost >= 0) {
              totalCost += roleCost;
            }
          });
          
          return {
            id: staffingMilestone.id,
            name: staffingMilestone.name,
            cost: Math.round(totalCost)
          };
        });
        
        console.log('OutcomeInfoStep: Calculated milestone costs:', milestonesWithCosts);
        
        // Update milestone costs in form data without triggering re-renders
        setFormData(prev => {
          const updatedMilestones = prev.milestones.map(milestone => {
            const costData = milestonesWithCosts.find(cm => cm.id === milestone.id);
            if (costData && milestone.cost !== costData.cost) {
              console.log(`OutcomeInfoStep: Updating milestone ${milestone.name} cost from ${milestone.cost} to ${costData.cost}`);
              return {
                ...milestone,
                cost: costData.cost
              };
            }
            return milestone;
          });
          
          // Only update if there are actual changes
          const hasChanges = updatedMilestones.some((milestone, index) => 
            milestone.cost !== prev.milestones[index]?.cost
          );
          
          if (hasChanges) {
            return {
              ...prev,
              milestones: updatedMilestones
            };
          }
          
          return prev;
        });
      } else {
        console.log('OutcomeInfoStep: No staffing milestones found, keeping costs at 0');
      }
    } catch (error) {
      console.log('OutcomeInfoStep: Could not fetch staffing data for milestone costs:', error.message);
      // This is expected if staffing hasn't been set up yet
    }
  }, [data?.id]); // Remove formData.milestones dependency to prevent infinite loop

  // Compute approved_budget as sum of department_budgets (for downstream consumers)
  const computedApprovedBudget = React.useMemo(() => ({
    capex: (formData.department_budgets || []).reduce((s, db) => s + (db.capex || 0), 0),
    opex: (formData.department_budgets || []).reduce((s, db) => s + (db.opex || 0), 0),
  }), [formData.department_budgets]);

  // Calculate if project is above CAPEX threshold
  const isAboveCapexThreshold = React.useMemo(() => {
    return (computedApprovedBudget.capex + computedApprovedBudget.opex) >= CAPEX_THRESHOLD;
  }, [computedApprovedBudget]);

  const hasBudgetSet = React.useMemo(() => {
    return (computedApprovedBudget.capex + computedApprovedBudget.opex) > 0;
  }, [computedApprovedBudget]);

      // Listen for plan approval events to update outcome status
  useEffect(() => {
    const handlePlanApproval = (event) => {
      const { outcomeIds } = event.detail;
      if (data && data.id && outcomeIds.includes(data.id)) {
        // Update the outcome status to "Funded" when its plan is approved
        const updatedData = { ...formData, status: 'Funded' };
        setFormData(updatedData);
        onUpdate(updatedData);
      }
    };

    const handleApprovedBudgetSync = (event) => {
      const { outcomeId, capex, opex } = event.detail;
      if (data && data.id && data.id === outcomeId) {
        // Update the approved budget when synced from plan
        const updatedData = {
          ...formData,
          approved_budget: {
            capex: capex || 0,
            opex: opex || 0
          }
        };
        setFormData(updatedData);
        onUpdate(updatedData);
      }
    };

    const handleApprovedBudgetCleared = (event) => {
      const { outcomeId } = event.detail;
      if (data && data.id && data.id === outcomeId) {
        // Clear the approved budget when reset from plan
        const updatedData = {
          ...formData,
          approved_budget: {
            capex: 0,
            opex: 0
          }
        };
        setFormData(updatedData);
        onUpdate(updatedData);
      }
        };

    window.addEventListener('planApproved', handlePlanApproval);
    window.addEventListener('outcomeApprovedBudgetSynced', handleApprovedBudgetSync);
    window.addEventListener('outcomeApprovedBudgetCleared', handleApprovedBudgetCleared);
    
    return () => {
      window.removeEventListener('planApproved', handlePlanApproval);
      window.removeEventListener('outcomeApprovedBudgetSynced', handleApprovedBudgetSync);
      window.removeEventListener('outcomeApprovedBudgetCleared', handleApprovedBudgetCleared);
    };
    }, [data, formData, onUpdate]);

  useEffect(() => {
    // Only initialize once or when data.id changes
    const dataId = data?.id || 'new';
    if (initializedRef.current === dataId) {
      return;
    }
    initializedRef.current = dataId;

    if (data) {
      const defaults = getDefaultDates();
      let status = data.status || 'To Do';
      if (!allowedStatuses.includes(status)) {
        status = 'To Do';
      }

      // Get the year from start_date or use next year as default
      const outcomeYear = data.start_date ? 
        dayjs(data.start_date).year() : 
        new Date().getFullYear() + 1;

      const initialData = {
        id: data.id || undefined,
        name: data.name || '',
        description: data.description || data.goal || '',
        risks: data.risks || '',
        dependencies: data.dependencies || '',
        status,
        start_date: data.start_date || defaults.start_date,
        end_date: data.end_date || defaults.end_date,
        approved_budget: {
          capex: data.approved_budget?.capex || 0,
          opex: data.approved_budget?.opex || 0
        },
        department_budgets: (Array.isArray(data.department_budgets) && data.department_budgets.length > 0)
          ? data.department_budgets
          : ((data.approved_budget?.capex > 0 || data.approved_budget?.opex > 0)
            ? [{ department_id: '8f6a66cb-4bf7-4dc1-9514-737114ffba0b', department_name: 'IT', capex: data.approved_budget.capex || 0, opex: data.approved_budget.opex || 0 }]
            : []),
                applications: data.applications || [],
        tags: data.tags || [],
        milestones: data.milestones && data.milestones.length > 0 ?
          data.milestones :
          [getDefaultMilestone(outcomeYear)],
        functional_owner: data.functional_owner || '',
        it_owner: data.it_owner || '',
        project_code: data.project_code || '',
        project_name: data.project_name || '',
        department: data.department || ''
      };

      setFormData(initialData);
      // Don't call onUpdate during initialization to prevent loops
    } else {
      const defaults = getDefaultDates();
      const outcomeYear = new Date().getFullYear() + 1;
      const defaultData = {
        name: '',
        description: '',
        risks: '',
        dependencies: '',
        status: 'To Do',
        start_date: defaults.start_date,
        end_date: defaults.end_date,
        approved_budget: {
          capex: 0,
          opex: 0
        },
        department_budgets: [],
        applications: [],
        tags: [],
        milestones: [getDefaultMilestone(outcomeYear)],
        functional_owner: '',
        it_owner: '',
        project_code: '',
        project_name: '',
        department: ''
      };
      setFormData(defaultData);
      // Don't call onUpdate during initialization to prevent loops
    }
  }, [data?.id, getDefaultMilestone]);

  // Update existing milestones when budget changes
  useEffect(() => {
    if (hasBudgetSet && formData.milestones && formData.milestones.length > 0) {
      const needsClassificationUpdate = formData.milestones.some(m => !m.capex_classification);
      const defaultClassification = isAboveCapexThreshold ? 'CAPEX' : 'OPEX';
      
      if (needsClassificationUpdate) {
        const updatedMilestones = formData.milestones.map(milestone => ({
          ...milestone,
          capex_classification: milestone.capex_classification || defaultClassification
        }));
        
        const updatedData = { ...formData, milestones: updatedMilestones };
        setFormData(updatedData);
        onUpdate(updatedData);
      }
    }
  }, [hasBudgetSet, isAboveCapexThreshold, formData.milestones, onUpdate]);
















      // Notify other components when milestones change (with debouncing to prevent spam)
  const milestoneChangeTimeoutRef = useRef(null);
  useEffect(() => {
    if (formData.milestones && formData.milestones.length > 0 && formData.id) {
      // Clear previous timeout
      if (milestoneChangeTimeoutRef.current) {
        clearTimeout(milestoneChangeTimeoutRef.current);
      }
      
      // Debounce the event dispatch
      milestoneChangeTimeoutRef.current = setTimeout(() => {
        const event = new CustomEvent('outcomeMilestonesChanged', {
          detail: {
            outcomeId: formData.id,
            milestones: formData.milestones
          }
        });
        window.dispatchEvent(event);
        console.log('OutcomeInfoStep: Dispatched milestone change event', {
          outcomeId: formData.id,
          milestones: formData.milestones.map(m => ({ id: m.id, name: m.name, cost: m.cost }))
        });
      }, 100); // 100ms debounce
    }
    
    return () => {
      if (milestoneChangeTimeoutRef.current) {
        clearTimeout(milestoneChangeTimeoutRef.current);
      }
    };
  }, [formData.milestones, formData.id]);

    // Fetch functional owners (memoized to prevent duplicate calls)
  const fetchFunctionalOwners = useCallback(async () => {
    try {
      console.log('OutcomeInfoStep: Fetching functional owners from API...');
      const startTime = performance.now();
      const response = await api.getOwners();
      const endTime = performance.now();
      console.log(`OutcomeInfoStep: Owners loaded in ${(endTime - startTime).toFixed(2)}ms`);
      
      const functionalOwnersList = response?.functional || [];
      console.log('OutcomeInfoStep: Processed functional owners:', functionalOwnersList.length, 'owners');
      setFunctionalOwners(functionalOwnersList);
      setItOwners(response?.it || []);
      
    } catch (error) {
      console.error('OutcomeInfoStep: Error fetching functional owners:', error);
      setFunctionalOwners([]);
    }
  }, []);

          // Fetch available applications (memoized to prevent duplicate calls)
  const fetchApplications = useCallback(async () => {
    try {
      console.log('OutcomeInfoStep: Fetching applications from API...');
      const startTime = performance.now();
      const response = await api.getApplications();
      const endTime = performance.now();
      console.log(`OutcomeInfoStep: Applications loaded in ${(endTime - startTime).toFixed(2)}ms`);
      
      let apps = [];
      if (Array.isArray(response)) {
        apps = response;
      } else if (response && Array.isArray(response.applications)) {
        apps = response.applications;
      } else {
        console.warn('OutcomeInfoStep: Unexpected applications response format:', response);
        apps = [];
      }
      
      console.log('OutcomeInfoStep: Processed applications:', apps.length, 'applications');
      setAvailableApplications(apps);
      
    } catch (error) {
      console.error('OutcomeInfoStep: Error fetching applications:', error);
      setAvailableApplications([]);
    }
  }, []);

  // Fetch available departments
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await api.getDepartments();
      let depts = [];
      if (Array.isArray(response)) {
        depts = response;
      } else if (response && Array.isArray(response.departments)) {
        depts = response.departments;
      }
      setAvailableDepartments(depts);
    } catch (error) {
      console.error('OutcomeInfoStep: Error fetching departments:', error);
      setAvailableDepartments([]);
    }
  }, []);

    // Fetch available tags (memoized to prevent duplicate calls)
  const fetchTags = useCallback(async () => {
    try {
      console.log('OutcomeInfoStep: Fetching tags from API...');
      const startTime = performance.now();
      const response = await api.getTags();
      const endTime = performance.now();
      console.log(`OutcomeInfoStep: Tags loaded in ${(endTime - startTime).toFixed(2)}ms`);
      
      let tags = [];
      if (Array.isArray(response)) {
        tags = response;
      } else if (response && Array.isArray(response.tags)) {
        tags = response.tags;
      } else {
        console.warn('OutcomeInfoStep: Unexpected tags response format:', response);
        tags = [];
      }
      
      console.log('OutcomeInfoStep: Processed tags:', tags.length, 'tags');
      setAvailableTags(tags);
      
    } catch (error) {
      console.error('OutcomeInfoStep: Error fetching tags:', error);
      setAvailableTags([]);
    }
  }, []);

                          // Fetch reference data only once on component mount
  const referenceDataFetchedRef = useRef(false);
  useEffect(() => {
    if (!referenceDataFetchedRef.current) {
      referenceDataFetchedRef.current = true;
      fetchApplications();
      fetchTags();
      fetchFunctionalOwners();
      fetchDepartments();
    }
  }, []); // Empty dependency array - only run once
  
    // Fetch milestone costs on component mount and when outcome ID changes (only once per ID)
  const lastFetchedIdRef = useRef(null);
  useEffect(() => {
    if (initializedRef.current && data?.id && lastFetchedIdRef.current !== data.id) {
      console.log('OutcomeInfoStep: Fetching milestone costs on mount/ID change');
      lastFetchedIdRef.current = data.id;
      fetchMilestoneCosts();
    }
  }, [data?.id, fetchMilestoneCosts]);

    // Listen for application updates from other components
  useEffect(() => {
    const handleApplicationUpdate = () => {
      console.log('Received application update event, refreshing applications...');
      fetchApplications();
    };

    window.addEventListener('applicationCreated', handleApplicationUpdate);
    window.addEventListener('applicationUpdated', handleApplicationUpdate);
    window.addEventListener('applicationDeleted', handleApplicationUpdate);
    
    return () => {
      window.removeEventListener('applicationCreated', handleApplicationUpdate);
      window.removeEventListener('applicationUpdated', handleApplicationUpdate);
      window.removeEventListener('applicationDeleted', handleApplicationUpdate);
    };
  }, [fetchApplications]);

  // Listen for tag updates from other components
  useEffect(() => {
    const handleTagUpdate = () => {
      console.log('Received tag update event, refreshing tags...');
      fetchTags();
    };

    window.addEventListener('tagCreated', handleTagUpdate);
    window.addEventListener('tagUpdated', handleTagUpdate);
    window.addEventListener('tagDeleted', handleTagUpdate);
    
    return () => {
      window.removeEventListener('tagCreated', handleTagUpdate);
      window.removeEventListener('tagUpdated', handleTagUpdate);
      window.removeEventListener('tagDeleted', handleTagUpdate);
    };
  }, [fetchTags]);

    // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      if (milestoneChangeTimeoutRef.current) {
        clearTimeout(milestoneChangeTimeoutRef.current);
      }
    };
  }, []);

      const {
    showUnsavedModal,
    handleSaveAndNavigate,
    handleNavigateWithoutSaving,
    handleCancelNavigation
  } = useUnsavedChanges(
    formData,
    async (dataToSave) => {
      // Delegate entirely to the parent's save handler (OutcomeWizard.handleSaveOutcome),
      // which owns the create-vs-update decision and the in-flight guard against duplicate creates.
      if (onSave) {
        const saved = await onSave(dataToSave);
        if (saved?.id) onUpdate({ ...dataToSave, ...saved });
      }
    }
  );

              // Handler for text fields that need smooth editing
  const handleTextFieldChange = useCallback((field, value) => {
    // Update local editing state immediately
    setLocalEditingValues(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handler for when user clicks out of text field (blur)
  const handleTextFieldBlur = useCallback((field) => {
    const value = localEditingValues[field];
    if (value !== undefined) {
      // Save the changes when user clicks out
      setFormData(prevData => {
        const updatedData = { ...prevData, [field]: value };
        onUpdate(updatedData);
        return updatedData;
      });
      // Clear local editing state after save
      setLocalEditingValues(prev => {
        const newState = { ...prev };
        delete newState[field];
        return newState;
      });
    }
  }, [localEditingValues, onUpdate]);

  // Handler for other fields (non-text)
  const handleChange = useCallback((field, value) => {
    setFormData(prevData => {
      let updatedData = { ...prevData };
      
      if (field.includes('.')) {
        // Handle nested fields like approved_budget.capex
        const [parent, child] = field.split('.');
        updatedData[parent] = {
          ...updatedData[parent],
          [child]: value
        };
      } else {
        updatedData[field] = value;
      }
      
      // Ensure status is always valid
      if (field === 'status' && !allowedStatuses.includes(value)) {
        updatedData.status = 'To Do';
      }
      
                  // Immediate update for non-text fields
      onUpdate(updatedData);
      
      return updatedData;
    });
  }, [onUpdate]);

  // Helper to get current value for text fields (local editing value or form data)
  const getTextFieldValue = useCallback((field) => {
    return localEditingValues.hasOwnProperty(field) ? localEditingValues[field] : (formData[field] || '');
  }, [localEditingValues, formData]);

  const handleSave = async () => {
    if (readOnly) return;
    
    setLocalLoading(true);
    try {
      const savedOutcome = await handleSaveAndNavigate();
      if (!savedOutcome || !savedOutcome.id) {
        alert("Failed to save outcome. Please try again.");
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLocalLoading(false);
    }
  };

  // Department budget handlers
  const handleDeptBudgetChange = useCallback((idx, field, value) => {
    setFormData(prev => {
      const updated = prev.department_budgets.map((db, i) =>
        i === idx ? { ...db, [field]: Math.max(0, Number(value) || 0) } : db
      );
      const totalCapex = updated.reduce((s, db) => s + (db.capex || 0), 0);
      const totalOpex = updated.reduce((s, db) => s + (db.opex || 0), 0);
      const next = { ...prev, department_budgets: updated, approved_budget: { capex: totalCapex, opex: totalOpex } };
      onUpdate(next);
      return next;
    });
  }, [onUpdate]);

  const handleAddDeptBudget = useCallback((deptId) => {
    const dept = availableDepartments.find(d => d.id === deptId);
    if (!dept) return;
    setFormData(prev => {
      if (prev.department_budgets.some(db => db.department_id === deptId)) return prev;
      const updated = [...prev.department_budgets, { department_id: dept.id, department_name: dept.name, capex: 0, opex: 0 }];
      const totalCapex = updated.reduce((s, db) => s + (db.capex || 0), 0);
      const totalOpex = updated.reduce((s, db) => s + (db.opex || 0), 0);
      const next = { ...prev, department_budgets: updated, approved_budget: { capex: totalCapex, opex: totalOpex } };
      onUpdate(next);
      return next;
    });
  }, [availableDepartments, onUpdate]);

  const handleDepartmentMultiChange = useCallback((newNames) => {
    const currentNames = (formData.department_budgets || []).map(db => db.department_name);
    const added = newNames.filter(n => !currentNames.includes(n));
    added.forEach(name => {
      const dept = availableDepartments.find(d => d.name === name);
      if (dept) handleAddDeptBudget(dept.id);
    });
    // Sync primary department field for backward compat with filters
    handleChange('department', newNames[0] || '');
  }, [formData.department_budgets, availableDepartments, handleAddDeptBudget, handleChange]);

  const handleRemoveDeptBudget = useCallback((idx) => {
    setFormData(prev => {
      const updated = prev.department_budgets.filter((_, i) => i !== idx);
      const totalCapex = updated.reduce((s, db) => s + (db.capex || 0), 0);
      const totalOpex = updated.reduce((s, db) => s + (db.opex || 0), 0);
      const next = { ...prev, department_budgets: updated, approved_budget: { capex: totalCapex, opex: totalOpex } };
      onUpdate(next);
      return next;
    });
  }, [onUpdate]);

  // Milestone management functions
  const handleAddMilestone = () => {
    const outcomeYear = formData.start_date ? 
      dayjs(formData.start_date).year() : 
      new Date().getFullYear() + 1;
    
    // Determine default CAPEX/OPEX classification
    let defaultClassification = 'OPEX';
    if (hasBudgetSet) {
      defaultClassification = isAboveCapexThreshold ? 'CAPEX' : 'OPEX';
    }
    
    setMilestoneForm({
      name: `Milestone #${formData.milestones.length + 1}`,
      start_date: `${outcomeYear}-01-01`,
      end_date: `${outcomeYear}-12-31`,
      cost: 0,
      capex_classification: defaultClassification
    });
    setEditingMilestone(null);
    setMilestoneDialogOpen(true);
  };

  const handleEditMilestone = (milestone, index) => {
    // Ensure existing milestones have a classification if budget is set
    let classification = milestone.capex_classification;
    if (!classification && hasBudgetSet) {
      classification = isAboveCapexThreshold ? 'CAPEX' : 'OPEX';
    }
    
    setMilestoneForm({
      name: milestone.name,
      start_date: milestone.start_date,
      end_date: milestone.end_date,
      cost: milestone.cost,
      capex_classification: classification || 'OPEX'
    });
    setEditingMilestone(index);
    setMilestoneDialogOpen(true);
  };

  const handleDeleteMilestone = (index) => {
    if (formData.milestones.length <= 1) {
      alert("At least one milestone is required.");
      return;
    }
    
    const updatedMilestones = formData.milestones.filter((_, i) => i !== index);
    const updatedData = { ...formData, milestones: updatedMilestones };
    setFormData(updatedData);
    onUpdate(updatedData);
  };

  const handleSaveMilestone = () => {
    if (!milestoneForm.name.trim()) {
      alert("Milestone name is required.");
      return;
    }

    const milestone = {
      id: editingMilestone !== null ? formData.milestones[editingMilestone].id : generateMilestoneId(),
      name: milestoneForm.name.trim(),
      start_date: milestoneForm.start_date,
      end_date: milestoneForm.end_date,
                              cost: editingMilestone !== null ?
        // For existing milestones, preserve the current cost (will be updated by useEffect)
        formData.milestones[editingMilestone].cost :
        // For new milestones, start with 0
        0,
      capex_classification: milestoneForm.capex_classification || 'OPEX'
    };

    let updatedMilestones;
    if (editingMilestone !== null) {
      updatedMilestones = formData.milestones.map((m, i) => 
        i === editingMilestone ? milestone : m
      );
    } else {
      updatedMilestones = [...formData.milestones, milestone];
    }

    const updatedData = { ...formData, milestones: updatedMilestones };
    setFormData(updatedData);
    onUpdate(updatedData);
    setMilestoneDialogOpen(false);
  };

        const handleMilestoneFormChange = (field, value) => {
    setMilestoneForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

        // Application management functions
  const handleAddNewApplication = async () => {
    if (!newApplicationForm.name.trim()) {
      alert('Application name is required.');
      return;
    }

    try {
      console.log('OutcomeInfoStep: Creating application with data:', newApplicationForm);
      const response = await api.createApplication(newApplicationForm);
      console.log('OutcomeInfoStep: Raw create application response:', response);
      
      // Extract the new application from the response
      let newApp = null;
      
      if (response && typeof response === 'object') {
        if (response.application) {
          // Backend returns: {message: "...", application: {...}}
          newApp = response.application;
        } else if (response.message && response.message.includes('success')) {
          // If we have a success message, try to find the application data
          // This handles cases where the structure might be different
          newApp = {
            id: response.application?.id || `temp-${Date.now()}`,
            name: newApplicationForm.name,
            status: newApplicationForm.status
          };
        }
      }
      
      console.log('OutcomeInfoStep: Processed application object:', newApp);
      
      // Validate the processed application
      if (!newApp || !newApp.id || !newApp.name) {
        console.error('OutcomeInfoStep: Could not extract valid application from response:', {
          response,
          processedApp: newApp,
          originalForm: newApplicationForm
        });
        
        // Refresh applications list to get the latest data
        console.log('OutcomeInfoStep: Refreshing applications list after creation...');
        await fetchApplications();
        
        // Close dialog and reset form
        setNewApplicationForm({ name: '', status: 'Active' });
        setNewApplicationDialogOpen(false);
        
        alert('Application created successfully! The applications list has been refreshed.');
        return;
      }
      
      console.log('OutcomeInfoStep: Valid new application created:', newApp);
      
      // Add the new application to the available list
      setAvailableApplications(prev => {
        // Check if app already exists to avoid duplicates
        const exists = prev.some(app => app.id === newApp.id);
        if (exists) {
          console.log('OutcomeInfoStep: Application already exists in list, not adding duplicate');
          return prev;
        }
        console.log('OutcomeInfoStep: Adding new application to available list');
        return [...prev, newApp];
      });
      
      // Add the new application to the current outcome's applications
      const updatedApplications = [...formData.applications, newApp.id];
      console.log('OutcomeInfoStep: Adding application to outcome applications:', updatedApplications);
      handleChange('applications', updatedApplications);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('applicationCreated', {
        detail: { applicationData: newApp }
      }));
      
      // Reset form and close dialog
      setNewApplicationForm({ name: '', status: 'Active' });
      setNewApplicationDialogOpen(false);
      
      console.log('OutcomeInfoStep: Successfully created and added new application:', newApp);
      
    } catch (error) {
      console.error('OutcomeInfoStep: Error creating application:', error);
      alert(`Failed to create application: ${error.message}`);
    }
  };

                const handleNewApplicationFormChange = (field, value) => {
    setNewApplicationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRemoveApplication = (applicationId) => {
    const updatedApplications = formData.applications.filter(id => id !== applicationId);
    handleChange('applications', updatedApplications);
  };

  // Tag management functions
  const handleAddNewTag = async () => {
    if (!newTagForm.name.trim()) {
      alert('Tag name is required.');
      return;
    }

    try {
      console.log('OutcomeInfoStep: Creating tag with data:', newTagForm);
      const response = await api.createTag(newTagForm);
      console.log('OutcomeInfoStep: Raw create tag response:', response);
      
      // Extract the new tag from the response
      let newTag = null;
      
      if (response && typeof response === 'object') {
        if (response.tag) {
          // Backend returns: {message: "...", tag: {...}}
          newTag = response.tag;
        } else if (response.message && response.message.includes('success')) {
          // If we have a success message, try to find the tag data
          newTag = {
            id: response.tag?.id || `temp-${Date.now()}`,
            name: newTagForm.name
          };
        }
      }
      
      console.log('OutcomeInfoStep: Processed tag object:', newTag);
      
      // Validate the processed tag
      if (!newTag || !newTag.id || !newTag.name) {
        console.error('OutcomeInfoStep: Could not extract valid tag from response:', {
          response,
          processedTag: newTag,
          originalForm: newTagForm
        });
        
        // Refresh tags list to get the latest data
        console.log('OutcomeInfoStep: Refreshing tags list after creation...');
        await fetchTags();
        
        // Close dialog and reset form
        setNewTagForm({ name: '' });
        setNewTagDialogOpen(false);
        
        alert('Tag created successfully! The tags list has been refreshed.');
        return;
      }
      
      console.log('OutcomeInfoStep: Valid new tag created:', newTag);
      
      // Add the new tag to the available list
      setAvailableTags(prev => {
        // Check if tag already exists to avoid duplicates
        const exists = prev.some(tag => tag.id === newTag.id);
        if (exists) {
          console.log('OutcomeInfoStep: Tag already exists in list, not adding duplicate');
          return prev;
        }
        console.log('OutcomeInfoStep: Adding new tag to available list');
        return [...prev, newTag];
      });
      
      // Add the new tag to the current outcome's tags
      const updatedTags = [...formData.tags, newTag.id];
      console.log('OutcomeInfoStep: Adding tag to outcome tags:', updatedTags);
      handleChange('tags', updatedTags);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('tagCreated', {
        detail: { tagData: newTag }
      }));
      
      // Reset form and close dialog
      setNewTagForm({ name: '' });
      setNewTagDialogOpen(false);
      
      console.log('OutcomeInfoStep: Successfully created and added new tag:', newTag);
      
    } catch (error) {
      console.error('OutcomeInfoStep: Error creating tag:', error);
      alert(`Failed to create tag: ${error.message}`);
    }
  };

  const handleNewTagFormChange = (field, value) => {
    setNewTagForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRemoveTag = (tagId) => {
    const updatedTags = formData.tags.filter(id => id !== tagId);
    handleChange('tags', updatedTags);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
      case 'Funded':
        return 'success';
      case 'In Progress':
      case 'Ready for Review':
      case 'Ready for ITGC':
        return 'primary';
      case 'Delayed':
      case 'Not Funded':
        return 'error';
      case 'Backlog':
        return 'warning';
      default:
        return 'default';
    }
  };

                                        // Memoized details section to prevent re-renders that steal focus
  const DetailsSection = React.useCallback(() => (
    <Box>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 2,
        p: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        borderRadius: 1
      }}>
        <AssessmentIcon sx={{ fontSize: '1.1rem', color: 'primary.main', mr: 1 }} />
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
          Outcome Details
        </Typography>
      </Box>

      {/* Description Card */}
      <Card elevation={1} sx={{ mb: 1.5 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Description
              </Typography>
            </Box>
          }
          sx={{ pb: 1, pt: 1.5 }}
        />
        <CardContent sx={{ pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <TextField
            value={getTextFieldValue('description')}
            onChange={e => handleTextFieldChange('description', e.target.value)}
            onBlur={() => handleTextFieldBlur('description')}
            placeholder="Add description, objectives, and expected deliverables..."
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            size="small"
            disabled={readOnly}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04)
                },
                '&.Mui-focused': {
                  bgcolor: alpha(theme.palette.primary.main, 0.06)
                }
              }
            }}
          />
        </CardContent>
      </Card>

            {/* Risks and Dependencies Row */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: '100%' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon sx={{ fontSize: '1.1rem', color: 'warning.main' }} />
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Risks
                  </Typography>
                </Box>
              }
              sx={{ pb: 1, pt: 1.5 }}
            />
            <CardContent sx={{ pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                                          <TextField
                value={getTextFieldValue('risks')}
                onChange={e => handleTextFieldChange('risks', e.target.value)}
                onBlur={() => handleTextFieldBlur('risks')}
                placeholder="Add potential risks and mitigation strategies..."
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                size="small"
                disabled={readOnly}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: alpha(theme.palette.warning.main, 0.02),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.warning.main, 0.04)
                    },
                    '&.Mui-focused': {
                      bgcolor: alpha(theme.palette.warning.main, 0.06)
                    }
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: '100%' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DependencyIcon sx={{ fontSize: '1.1rem', color: 'info.main' }} />
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Dependencies
                  </Typography>
                </Box>
              }
              sx={{ pb: 1, pt: 1.5 }}
            />
            <CardContent sx={{ pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                                          <TextField
                value={getTextFieldValue('dependencies')}
                onChange={e => handleTextFieldChange('dependencies', e.target.value)}
                onBlur={() => handleTextFieldBlur('dependencies')}
                placeholder="Add external dependencies and prerequisites..."
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                size="small"
                disabled={readOnly}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: alpha(theme.palette.info.main, 0.02),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.info.main, 0.04)
                    },
                    '&.Mui-focused': {
                      bgcolor: alpha(theme.palette.info.main, 0.06)
                    }
                  }
                }}
              />
            </CardContent>
          </Card>
                </Grid>
      </Grid>

            {/* Project Information Card */}
      <Card elevation={1} sx={{ mt: 1.5 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon sx={{ fontSize: '1.1rem', color: 'secondary.main' }} />
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Project Information
              </Typography>
            </Box>
          }
          sx={{ pb: 1, pt: 1.5 }}
        />
        <CardContent sx={{ pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Project Code"
                value={getTextFieldValue('project_code')}
                onChange={e => handleTextFieldChange('project_code', e.target.value)}
                onBlur={() => handleTextFieldBlur('project_code')}
                placeholder="Enter project code for spend tracking..."
                fullWidth
                variant="outlined"
                size="small"
                disabled={readOnly}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: alpha(theme.palette.secondary.main, 0.02),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.secondary.main, 0.04)
                    },
                    '&.Mui-focused': {
                      bgcolor: alpha(theme.palette.secondary.main, 0.06)
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Project Name"
                value={getTextFieldValue('project_name')}
                onChange={e => handleTextFieldChange('project_name', e.target.value)}
                onBlur={() => handleTextFieldBlur('project_name')}
                placeholder="Enter descriptive project name..."
                fullWidth
                variant="outlined"
                size="small"
                disabled={readOnly}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: alpha(theme.palette.secondary.main, 0.02),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.secondary.main, 0.04)
                    },
                    '&.Mui-focused': {
                      bgcolor: alpha(theme.palette.secondary.main, 0.06)
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Project Code is used for spend tracking and must match the code in uploaded spend data.
          </Typography>
        </CardContent>
      </Card>

      {/* Applications Card */}
      <Card 
        elevation={1} 
        sx={{ 
          mt: 1.5,
          background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.04)} 100%)`,
          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          '&:hover': {
            borderColor: alpha(theme.palette.info.main, 0.3)
          }
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DependencyIcon sx={{ fontSize: '1.1rem', color: 'info.main' }} />
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Applications ({formData.applications.length})
              </Typography>
            </Box>
          }
          sx={{ pb: 1, pt: 1.5 }}
        />
        <CardContent sx={{ pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
          <FormControl fullWidth size="small">
            <InputLabel>Select Applications</InputLabel>
            <Select
              multiple
              value={formData.applications}
              label="Select Applications"
              onChange={e => handleChange('applications', e.target.value)}
              disabled={readOnly}
                                                                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected
                          .filter(value => value !== '__add_new__') // Filter out the add new option
                          .map((value) => {
                            const app = availableApplications.find(a => a.id === value);
                            return (
                              <Chip
                                key={value}
                                label={app?.name || value}
                                size="small"
                                color="info"
                                variant="outlined"
                                                                                                onDelete={readOnly ? undefined : (e) => {
                                  e && e.stopPropagation && e.stopPropagation(); // Prevent Select dropdown from opening
                                  handleRemoveApplication(value);
                                }}
                                deleteIcon={<Box sx={{ fontSize: '0.8rem' }}>×</Box>}
                                sx={{
                                  '& .MuiChip-deleteIcon': {
                                    fontSize: '1rem',
                                    '&:hover': {
                                      color: 'error.main'
                                    }
                                  }
                                }}
                              />
                            );
                          })}
                      </Box>
                    )}
                            sx={{
                '& .MuiSelect-select': {
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
                            {/* Add New Application Option */}
                <MenuItem 
    onClick={(e) => {
      e && e.stopPropagation && e.stopPropagation();
      setNewApplicationDialogOpen(true);
    }}
                sx={{ 
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  mb: 1,
                  fontStyle: 'italic',
                  color: 'primary.main'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddIcon sx={{ fontSize: '1rem' }} />
                  <Typography>Add New Application...</Typography>
                </Box>
              </MenuItem>
              
              {/* Existing Applications */}
              {availableApplications.map((app) => (
                <MenuItem key={app.id} value={app.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={app.status}
                      size="small"
                      color={app.status === 'Active' ? 'success' : 
                             app.status === 'Maintenance' ? 'warning' : 
                             app.status === 'Sunsetting' ? 'error' : 'default'}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                    <Typography>{app.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
                    {formData.applications.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              No applications selected. Choose applications that this outcome will impact.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Tags Card */}
      <Card 
        elevation={1} 
        sx={{ 
          mt: 1.5,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[500], 0.08)} 0%, ${alpha(theme.palette.grey[500], 0.04)} 100%)`,
          border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
          '&:hover': {
            borderColor: alpha(theme.palette.grey[500], 0.3)
          }
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'grey.500'
                }}
              />
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Tags ({formData.tags.length})
              </Typography>
            </Box>
          }
          sx={{ pb: 1, pt: 1.5 }}
        />
        <CardContent sx={{ pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
          <FormControl fullWidth size="small">
            <InputLabel>Select Tags</InputLabel>
            <Select
              multiple
              value={formData.tags}
              label="Select Tags"
              onChange={e => handleChange('tags', e.target.value)}
              disabled={readOnly}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected
                    .filter(value => value !== '__add_new__') // Filter out the add new option
                    .map((value) => {
                      const tag = availableTags.find(t => t.id === value);
                      return (
                        <Chip
                          key={value}
                          label={tag?.name || value}
                          size="small"
                          sx={{
                            bgcolor: '#e0e0e0',
                            color: '#424242',
                            '&:hover': { bgcolor: '#d5d5d5' }
                          }}
                          variant="filled"
                            onDelete={readOnly ? undefined : (e) => {
    e && e.stopPropagation && e.stopPropagation(); // Prevent Select dropdown from opening
    handleRemoveTag(value);
  }}
                          deleteIcon={<Box sx={{ fontSize: '0.8rem' }}>×</Box>}
                        />
                      );
                    })}
                </Box>
              )}
              sx={{
                '& .MuiSelect-select': {
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              {/* Add New Tag Option */}
                              <MenuItem 
                  onClick={(e) => {
                    e && e.stopPropagation && e.stopPropagation();
                    setNewTagDialogOpen(true);
                  }}
                sx={{ 
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  mb: 1,
                  fontStyle: 'italic',
                  color: 'grey.600'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddIcon sx={{ fontSize: '1rem' }} />
                  <Typography>Add New Tag...</Typography>
                </Box>
              </MenuItem>
              
              {/* Existing Tags */}
              {availableTags.map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={tag.name}
                      size="small"
                      sx={{
                        bgcolor: '#e0e0e0',
                        color: '#424242',
                        fontSize: '0.7rem',
                        height: 20
                      }}
                      variant="filled"
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {formData.tags.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              No tags selected. Add tags to categorize and organize this outcome.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  ), [formData.description, formData.risks, formData.dependencies, formData.applications, formData.tags, availableApplications, availableTags, handleTextFieldChange, handleTextFieldBlur, getTextFieldValue, handleChange, handleRemoveApplication, handleRemoveTag, readOnly, theme, setNewApplicationDialogOpen, setNewTagDialogOpen]);

        // Validation check
  const isValid = formData.name.trim() !== '';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
        {/* Header: Outcome Details + Owners, side by side */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Left card — Outcome Details (60%) */}
          <Grid item xs={12} md={7}>
            <Card
              elevation={1}
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
              }}
            >
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    Outcome Details
                  </Typography>
                }
                subheader={
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                    Define the basic details and timeline for your outcome
                  </Typography>
                }
                sx={{ pb: 1, pt: 1.5 }}
              />
              <CardContent sx={{ pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={2}>
              {/* Name Row */}
              <Grid item xs={12} md={8}>
                <TextField
                  label="Outcome Name"
                  value={formData.name}
                  onChange={e => handleChange('name', e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled={readOnly}
                  sx={{ 
                    '& .MuiInputBase-root': { height: 40 },
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                  }}
                                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                  <Select
                    value={allowedStatuses.includes(formData.status) ? formData.status : 'To Do'}
                    label="Status"
                    onChange={e => handleChange('status', e.target.value)}
                    disabled={readOnly}
                    sx={{ height: 40 }}
                  >
                    {allowedStatuses.map(status => (
                      <MenuItem key={status} value={status} sx={{ fontSize: '0.875rem' }}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Department(s)</InputLabel>
                  <Select
                    multiple
                    value={(formData.department_budgets || []).map(db => db.department_name)}
                    label="Department(s)"
                    onChange={e => handleDepartmentMultiChange(e.target.value)}
                    disabled={readOnly}
                    sx={{ height: 40 }}
                    renderValue={(selected) =>
                      selected.length === 0 ? '' :
                      selected.length === 1 ? selected[0] :
                      `${selected.length} departments`
                    }
                  >
                    {availableDepartments.map(dept => (
                      <MenuItem key={dept.id} value={dept.name} sx={{ fontSize: '0.875rem' }}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Timeline Row */}
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Start Date"
                  value={formData.start_date ? dayjs(formData.start_date) : null}
                  onChange={date => handleChange('start_date', date ? date.format('YYYY-MM-DD') : '')}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      size: "small", 
                      disabled: readOnly,
                      sx: { '& .MuiInputBase-root': { height: 40 } }
                    } 
                  }}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="End Date"
                  value={formData.end_date ? dayjs(formData.end_date) : null}
                  onChange={date => handleChange('end_date', date ? date.format('YYYY-MM-DD') : '')}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      size: "small", 
                      disabled: readOnly,
                      sx: { '& .MuiInputBase-root': { height: 40 } }
                    } 
                  }}
                                    disabled={readOnly}
                />
              </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right card — Owners (40%) */}
          <Grid item xs={12} md={5}>
            <Card
              elevation={1}
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`
              }}
            >
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main' }} />
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Owners</Typography>
                  </Box>
                }
                sx={{ pb: 1, pt: 1.5 }}
              />
              <CardContent sx={{ pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Functional Owner</InputLabel>
                    <Select
                      value={formData.functional_owner || ''}
                      label="Functional Owner"
                      onChange={e => handleChange('functional_owner', e.target.value)}
                      disabled={readOnly}
                      sx={{ height: 40 }}
                    >
                      <MenuItem value="" sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                        <em>No Owner Selected</em>
                      </MenuItem>
                      {functionalOwners.map(owner => (
                        <MenuItem key={owner.id} value={owner.id} sx={{ fontSize: '0.875rem' }}>
                          {owner.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.875rem' }}>IT Owner</InputLabel>
                    <Select
                      value={formData.it_owner || ''}
                      label="IT Owner"
                      onChange={e => handleChange('it_owner', e.target.value)}
                      disabled={readOnly}
                      sx={{ height: 40 }}
                    >
                      <MenuItem value="" sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                        <em>No Owner Selected</em>
                      </MenuItem>
                      {itOwners.map(owner => (
                        <MenuItem key={owner.id} value={owner.id} sx={{ fontSize: '0.875rem' }}>
                          {owner.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content Grid */}
        <Grid container spacing={2}>
          {/* Left Column - Description and Details */}
          <Grid item xs={12} lg={8}>
            {/* Details Section */}
            {DetailsSection()}
          </Grid>

                    {/* Right Column - Budget and Milestones */}
          <Grid item xs={12} lg={4}>
            {/* Budget Card */}
            <Card 
              elevation={1} 
              sx={{ 
                mb: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.04)} 100%)`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                '&:hover': {
                  borderColor: alpha(theme.palette.success.main, 0.3)
                }
              }}
            >
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'success.main'
                      }}
                    />
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                      Approved Budget
                    </Typography>
                    {/* Show sync indicator if values were synced from plan */}
                    {hasBudgetSet && (
                      <Tooltip title="Budget values synced from approved plan">
                        <SyncIcon sx={{ fontSize: '0.875rem', color: 'primary.main', opacity: 0.7 }} />
                      </Tooltip>
                    )}
                  </Box>
                }
                sx={{ pb: 1, pt: 1.5 }}
              />
              <CardContent sx={{ pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                {/* Department budget table */}
                {formData.department_budgets.length > 0 && (
                  <Table size="small" sx={{ mb: 1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>Department</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5 }}>CAPEX</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5 }}>OPEX</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.5 }}>Total</TableCell>
                        {!readOnly && <TableCell sx={{ py: 0.5 }} />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.department_budgets.map((db, idx) => {
                        const selectedDeptNames = (formData.department_budgets || []).map(d => d.department_name);
                        // A row is "orphaned" if this dept was removed from the multi-select.
                        // Since the multi-select value IS department_budgets, orphaned rows can only
                        // exist when legacy data has a budget row without a matching dept_name.
                        // We detect mismatches by checking if the dept name exists in availableDepartments
                        // but is absent from the multi-select value (which equals selectedDeptNames itself —
                        // so instead flag rows where department_id is missing, indicating old string-only data).
                        const isOrphaned = !db.department_id && db.department_name &&
                          !availableDepartments.some(d => d.name === db.department_name);
                        return (
                        <TableRow key={db.department_id || db.department_name}>
                          <TableCell sx={{ py: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" fontWeight={500}>{db.department_name}</Typography>
                              {isOrphaned && (
                                <Tooltip title="Department not recognized — it may have been removed from Admin">
                                  <WarningIcon color="warning" sx={{ fontSize: '0.9rem' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            <TextField
                              type="number"
                              value={db.capex}
                              onChange={e => handleDeptBudgetChange(idx, 'capex', e.target.value)}
                              size="small"
                              disabled={readOnly}
                              inputProps={{ min: 0, style: { textAlign: 'right', width: 90, fontSize: '0.8rem' } }}
                              InputProps={{ startAdornment: <Typography sx={{ fontSize: '0.75rem', mr: 0.25 }}>$</Typography> }}
                              sx={{ '& .MuiInputBase-root': { height: 28 } }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            <TextField
                              type="number"
                              value={db.opex}
                              onChange={e => handleDeptBudgetChange(idx, 'opex', e.target.value)}
                              size="small"
                              disabled={readOnly}
                              inputProps={{ min: 0, style: { textAlign: 'right', width: 90, fontSize: '0.8rem' } }}
                              InputProps={{ startAdornment: <Typography sx={{ fontSize: '0.75rem', mr: 0.25 }}>$</Typography> }}
                              sx={{ '& .MuiInputBase-root': { height: 28 } }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            <Typography variant="body2" color="success.main" fontWeight={600}>
                              {formatCurrency((db.capex || 0) + (db.opex || 0))}
                            </Typography>
                          </TableCell>
                          {!readOnly && (
                            <TableCell sx={{ py: 0.5, pl: 0.5 }}>
                              <IconButton size="small" color="error" onClick={() => handleRemoveDeptBudget(idx)}>
                                <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                      })}
                      {/* Total row */}
                      <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                        <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: '0.8rem' }}>Total</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.8rem', fontWeight: 600 }}>{formatCurrency(computedApprovedBudget.capex)}</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.8rem', fontWeight: 600 }}>{formatCurrency(computedApprovedBudget.opex)}</TableCell>
                        <TableCell align="right" sx={{ py: 0.5 }}>
                          <Typography variant="body2" color="success.main" fontWeight={700}>
                            {formatCurrency(computedApprovedBudget.capex + computedApprovedBudget.opex)}
                          </Typography>
                        </TableCell>
                        {!readOnly && <TableCell />}
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
                {formData.department_budgets.length === 0 && readOnly && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No department budgets set
                  </Typography>
                )}
                {hasBudgetSet && (
                  <Box sx={{ px: 0, pt: 1 }}>
                    <Typography variant="caption" color="primary.main" sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.7rem',
                      fontStyle: 'italic'
                    }}>
                      <SyncIcon sx={{ fontSize: '0.7rem' }} />
                      Values may be synced from approved plan
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Milestones Card */}
            <Card 
              elevation={1} 
              sx={{ 
                mb: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                '&:hover': {
                  borderColor: alpha(theme.palette.secondary.main, 0.3)
                }
              }}
            >
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon sx={{ fontSize: '1.1rem', color: 'secondary.main' }} />
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                      Milestones ({formData.milestones.length})
                    </Typography>
                  </Box>
                }
                action={
                  !readOnly && (
                    <IconButton
                      onClick={handleAddMilestone}
                      size="small"
                      sx={{ 
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                        color: 'secondary.main'
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )
                }
                sx={{ pb: 1, pt: 1.5 }}
              />
              <CardContent sx={{ pt: 0, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                                {hasBudgetSet && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    {isAboveCapexThreshold 
                      ? `Budget ≥ $${CAPEX_THRESHOLD.toLocaleString()}: CAPEX/OPEX selection available`
                      : `Budget < $${CAPEX_THRESHOLD.toLocaleString()}: Milestones default to OPEX but can be changed`
                    }
                  </Typography>
                )}
                
                                {/* Vertical stack of milestones */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {formData.milestones
                    .sort((a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf())
                    .map((milestone, index) => (
                    <Box
                      key={milestone.id}
                      sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.secondary.main, 0.2),
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.secondary.main, 0.03),
                        '&:hover': { 
                          bgcolor: alpha(theme.palette.secondary.main, 0.08),
                          borderColor: alpha(theme.palette.secondary.main, 0.3)
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', flex: 1 }}>
                          {milestone.name}
                        </Typography>
                        {!readOnly && (
                          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditMilestone(milestone, index)}
                              sx={{ p: 0.25, color: 'secondary.main' }}
                            >
                              <EditIcon sx={{ fontSize: '0.875rem' }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteMilestone(index)}
                              disabled={formData.milestones.length <= 1}
                              sx={{ p: 0.25, color: 'error.main' }}
                            >
                              <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {milestone.start_date ? dayjs(milestone.start_date).format('MMM DD, YYYY') : '-'} - {' '}
                        {milestone.end_date ? dayjs(milestone.end_date).format('MMM DD, YYYY') : '-'}
                      </Typography>
                      




















                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {formatCurrency(milestone.cost || 0)}
                          </Typography>
                          {milestone.cost > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              (from staffing)
                            </Typography>
                          )}
                          {milestone.cost === 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                              (no staffing assigned)
                            </Typography>
                          )}
                        </Box>
                        {hasBudgetSet && (
                          <Chip
                            label={milestone.capex_classification || 'OPEX'}
                            size="small"
                            color={milestone.capex_classification === 'CAPEX' ? 'primary' : 'success'}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

                {/* Save Button */}
        {!hideButtons && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isValid || loading || localLoading || readOnly}
              startIcon={localLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ fontSize: '0.875rem', py: 1, px: 3 }}
            >
              {localLoading ? 'Saving...' : 'Save & Continue'}
            </Button>
          </Box>
        )}

        <UnsavedChangesModal
          open={showUnsavedModal}
          onSaveAndNavigate={handleSaveAndNavigate}
          onNavigateWithoutSaving={handleNavigateWithoutSaving}
          onCancel={handleCancelNavigation}
                />

        {/* Milestone Dialog */}
        <Dialog open={milestoneDialogOpen} onClose={() => setMilestoneDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: '1.1rem', pb: 1 }}>
            {editingMilestone !== null ? 'Edit Milestone' : 'Add Milestone'}
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  label="Milestone Name"
                  value={milestoneForm.name}
                  onChange={e => handleMilestoneFormChange('name', e.target.value)}
                  fullWidth
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={milestoneForm.start_date ? dayjs(milestoneForm.start_date) : null}
                  onChange={date => handleMilestoneFormChange('start_date', date ? date.format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={milestoneForm.end_date ? dayjs(milestoneForm.end_date) : null}
                  onChange={date => handleMilestoneFormChange('end_date', date ? date.format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </Grid>



















                            <Grid item xs={12} sm={6}>
                <TextField
                  label="Cost"
                  type="number"
                  value={milestoneForm.cost || 0}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.875rem' }}>$</Typography>,
                    readOnly: true,
                  }}
                  helperText="Cost is automatically calculated from staffing assignments"
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: theme.palette.action.hover,
                      cursor: 'default'
                    }
                  }}
                />
              </Grid>
                            {hasBudgetSet && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>CAPEX/OPEX Classification</InputLabel>
                    <Select
                      value={milestoneForm.capex_classification || 'OPEX'}
                      label="CAPEX/OPEX Classification"
                      onChange={e => handleMilestoneFormChange('capex_classification', e.target.value)}
                    >
                      <MenuItem value="CAPEX">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: 'primary.main'
                            }}
                          />
                          CAPEX
                        </Box>
                      </MenuItem>
                      <MenuItem value="OPEX">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: 'success.main'
                            }}
                          />
                          OPEX
                        </Box>
                      </MenuItem>
                    </Select>
                    {!isAboveCapexThreshold && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                        Defaults to OPEX for budgets under ${CAPEX_THRESHOLD.toLocaleString()}, but can be changed
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ pt: 1, pb: 2 }}>
                        <Button onClick={() => setMilestoneDialogOpen(false)} size="small">Cancel</Button>
            <Button onClick={handleSaveMilestone} variant="contained" size="small">
              {editingMilestone !== null ? 'Update' : 'Add'} Milestone
            </Button>
          </DialogActions>
        </Dialog>

                {/* Add New Application Dialog */}
        <Dialog open={newApplicationDialogOpen} onClose={() => setNewApplicationDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: '1.1rem', pb: 1 }}>
            Add New Application
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  label="Application Name"
                  value={newApplicationForm.name}
                  onChange={e => handleNewApplicationFormChange('name', e.target.value)}
                  fullWidth
                  required
                  size="small"
                  placeholder="Enter application name..."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newApplicationForm.status}
                    label="Status"
                    onChange={e => handleNewApplicationFormChange('status', e.target.value)}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Maintenance">Maintenance</MenuItem>
                    <MenuItem value="Sunsetting">Sunsetting</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ pt: 1, pb: 2 }}>
            <Button onClick={() => setNewApplicationDialogOpen(false)} size="small">Cancel</Button>
            <Button onClick={handleAddNewApplication} variant="contained" size="small">
              Add Application
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add New Tag Dialog */}
        <Dialog open={newTagDialogOpen} onClose={() => setNewTagDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: '1.1rem', pb: 1 }}>
            Add New Tag
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  label="Tag Name"
                  value={newTagForm.name}
                  onChange={e => handleNewTagFormChange('name', e.target.value)}
                  fullWidth
                  required
                  size="small"
                  placeholder="Enter tag name..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ pt: 1, pb: 2 }}>
            <Button onClick={() => setNewTagDialogOpen(false)} size="small">Cancel</Button>
            <Button onClick={handleAddNewTag} variant="contained" size="small">
              Add Tag
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

export default OutcomeInfoStep;